"use client";

import React from "react";
import { Label } from "../../label.js";
import { Input } from "../../input.js";
import { Textarea } from "../../textarea.js";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../select.js";
import { Button } from "../../button.js";
import { Alert, AlertDescription } from "../../alert.js";
import { Video, MapPin, Info } from "lucide-react";
import { MeetingFormData, CalendarPlatform } from "../types/meeting.js";
import { FormSectionCard } from "./FormSectionCard.js";
import { DURATION_OPTIONS, PLATFORM_OPTIONS, TEAM_MEMBERS } from "../constants/meetingConstants.js";
import { AttendeeChips } from "../attendees/AttendeeChips.js";
import { AttendeeSelector } from "../attendees/AttendeeSelector.js";
import { AttendeeSelectWithModal } from "../attendees/AttendeeSelectWithModal.js";
import { AlertCircle } from "lucide-react";

interface MeetingFormFieldsProps {
  formData: MeetingFormData;
  onInputChange: (field: keyof MeetingFormData, value: string | number | boolean) => void;
  onAddTeamMember: (email: string) => void;
  onRemoveAttendee: (email: string) => void;
  currentAttendees: string[];
  onAttendeesChange: (emails: string[]) => void;
  onOpenAttendeeInfo: () => void;
  isSubmitting: boolean;
  submitError: string | null;
  /** Platforms the user can create on; when omitted all options are shown. */
  availablePlatforms?: CalendarPlatform[];
  renderAttendeesSelector?: (args: {
    currentEmails: string[];
    setEmails: (emails: string[]) => void;
    disabled: boolean;
  }) => React.ReactNode;
  contacts?: Array<{ id: string; name: string; email: string; company_name?: string }>;
  onSearchContacts?: (searchTerm: string) => Promise<Array<{ id: string; name: string; email: string; company_name?: string }>>;
  isLoadingContacts?: boolean;
  ContactPopoverComponent?: React.ComponentType<{ name: string; email: string; children: React.ReactNode }>;
}

export const MeetingFormFields: React.FC<MeetingFormFieldsProps> = ({
  formData,
  onInputChange,
  onAddTeamMember,
  onRemoveAttendee,
  currentAttendees,
  onAttendeesChange,
  onOpenAttendeeInfo,
  isSubmitting,
  submitError,
  availablePlatforms,
  renderAttendeesSelector,
  contacts,
  onSearchContacts,
  isLoadingContacts,
  ContactPopoverComponent,
}) => {
  const platformOptions = availablePlatforms
    ? PLATFORM_OPTIONS.filter((option) => availablePlatforms.includes(option.value))
    : PLATFORM_OPTIONS;
  return (
    <>
      {submitError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{submitError}</AlertDescription>
        </Alert>
      )}

      {/* Two-column grid on desktop: compact controls pair up, wide fields
          (title, date/time, description, attendees) span the full width. */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {/* Platform Selection */}
      <FormSectionCard>
        <Label htmlFor="platform" className="text-slate-900 font-semibold">
          Calendar Platform
        </Label>
        <Select
          value={formData.platform}
          onValueChange={(value: CalendarPlatform) =>
            onInputChange("platform", value)
          }
        >
          <SelectTrigger className="border-gray-300 focus:border-blue-500 focus:ring-blue-500 hover:border-gray-400 transition-colors mt-3">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-card border border-gray-200 shadow-lg z-[99999]">
            {platformOptions.map((option) => (
              <SelectItem
                key={option.value}
                value={option.value}
                className="hover:bg-card-50 focus:bg-card-50"
              >
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FormSectionCard>

      {/* Meeting Title */}
      <div className="md:col-span-2">
      <FormSectionCard>
        <Label htmlFor="title" className="text-slate-900 font-semibold">
          Meeting Title *
        </Label>
        <Input
          id="title"
          value={formData.title}
          onChange={(e) => onInputChange("title", e.target.value)}
          placeholder="Enter meeting title"
          required
          className="border-gray-300 focus:border-blue-500 focus:ring-blue-500 hover:border-gray-400 transition-colors mt-3"
        />
      </FormSectionCard>
      </div>

      {/* Description */}
      <div className="md:col-span-2">
      <FormSectionCard>
        <Label htmlFor="description" className="text-slate-900 font-semibold">
          Description
        </Label>
        <Textarea
          id="description"
          value={formData.description}
          onChange={(e) => onInputChange("description", e.target.value)}
          placeholder="Enter meeting description or agenda"
          rows={3}
          className="border-gray-300 focus:border-blue-500 focus:ring-blue-500 hover:border-gray-400 transition-colors resize-none mt-3"
        />
      </FormSectionCard>
      </div>

      {/* Date and Time */}
      <div className="md:col-span-2">
      <FormSectionCard>
        <Label className="text-slate-900 font-semibold mb-4 block">
          Date & Time *
        </Label>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="startDate" className="text-slate-800">
              Date
            </Label>
            <Input
              id="startDate"
              type="date"
              value={formData.startDate}
              onChange={(e) => onInputChange("startDate", e.target.value)}
              required
              className="border-gray-300 focus:border-blue-500 focus:ring-blue-500 hover:border-gray-400 transition-colors"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="startTime" className="text-slate-800">
              Start Time
            </Label>
            <Input
              id="startTime"
              type="time"
              value={formData.startTime}
              onChange={(e) => onInputChange("startTime", e.target.value)}
              required
              className="border-gray-300 focus:border-blue-500 focus:ring-blue-500 hover:border-gray-400 transition-colors"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="duration" className="text-slate-800">
              Duration
            </Label>
            <Select
              value={formData.duration.toString()}
              onValueChange={(value: string) =>
                onInputChange("duration", parseInt(value))
              }
            >
              <SelectTrigger className="border-gray-300 focus:border-blue-500 focus:ring-blue-500 hover:border-gray-400 transition-colors">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-card border border-gray-200 shadow-lg z-[99999]">
                {DURATION_OPTIONS.map((option) => (
                  <SelectItem
                    key={option.value}
                    value={option.value.toString()}
                    className="hover:bg-card-50 focus:bg-card-50"
                  >
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </FormSectionCard>
      </div>

      {/* Meeting Type */}
      <FormSectionCard>
        <Label className="text-slate-900 font-semibold mb-4 block">
          Meeting Type
        </Label>
        <div className="flex gap-4">
          <Button
            type="button"
            variant={formData.isOnline ? "default" : "outline"}
            onClick={() => onInputChange("isOnline", true)}
            className={`flex items-center gap-2 transition-colors ${
              formData.isOnline
                ? "bg-blue-600 hover:bg-blue-700 text-white shadow-md"
                : "border-gray-300 text-slate-700 hover:bg-card-50 hover:border-gray-400"
            }`}
          >
            <Video className="h-4 w-4" />
            Online Meeting
          </Button>
          <Button
            type="button"
            variant={!formData.isOnline ? "default" : "outline"}
            onClick={() => onInputChange("isOnline", false)}
            className={`flex items-center gap-2 transition-colors ${
              !formData.isOnline
                ? "bg-blue-600 hover:bg-blue-700 text-white shadow-md"
                : "border-gray-300 text-slate-700 hover:bg-card-50 hover:border-gray-400"
            }`}
          >
            <MapPin className="h-4 w-4" />
            In-Person
          </Button>
        </div>

        {/* Online Meeting Notice */}
        {formData.isOnline && (
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start gap-2">
              <Video className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-blue-800">
                <p className="font-medium">Online Meeting Link</p>
                <p>
                  A meeting link will be automatically generated and included
                  in the calendar invite for all attendees.
                </p>
              </div>
            </div>
          </div>
        )}
      </FormSectionCard>

      {/* Location */}
      {!formData.isOnline && (
        <FormSectionCard>
          <Label htmlFor="location" className="text-slate-900 font-semibold">
            Location
          </Label>
          <Input
            id="location"
            value={formData.location}
            onChange={(e) => onInputChange("location", e.target.value)}
            placeholder="Enter meeting location"
            className="border-gray-300 focus:border-blue-500 focus:ring-blue-500 hover:border-gray-400 transition-colors mt-3"
          />
        </FormSectionCard>
      )}

      {/* Attendees */}
      <div className="md:col-span-2">
      <FormSectionCard>
        <div className="flex items-center justify-between mb-3">
          <Label htmlFor="attendees" className="text-slate-900 font-semibold">
            Attendees
          </Label>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onOpenAttendeeInfo}
            className="text-blue-600 hover:text-blue-800 hover:bg-blue-50"
          >
            <Info className="h-4 w-4 mr-1" />
            Help
          </Button>
        </div>
        <div className="space-y-2">
          {renderAttendeesSelector ? (
            <AttendeeSelector
              currentEmails={currentAttendees}
              onChange={onAttendeesChange}
              disabled={isSubmitting}
              externalSelector={renderAttendeesSelector}
            />
          ) : (
            <AttendeeSelectWithModal
              currentEmails={currentAttendees}
              onChange={onAttendeesChange}
              disabled={isSubmitting}
              placeholder="Search contacts or enter email addresses..."
              contacts={contacts}
              onSearchContacts={onSearchContacts}
              isLoadingContacts={isLoadingContacts}
            />
          )}

          {/* Quick Add Team Members */}
          <div className="space-y-2">
            <Label className="text-sm text-gray-600">
              Quick Add Team Members:
            </Label>
            <div className="flex flex-wrap gap-2">
              {TEAM_MEMBERS.map((member) => (
                <Button
                  key={member.email}
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => onAddTeamMember(member.email)}
                  disabled={currentAttendees.includes(member.email)}
                  className="text-xs"
                >
                  {member.name}
                </Button>
              ))}
            </div>
          </div>

          {/* Current Attendees */}
          {currentAttendees.length > 0 && (
            <div className="space-y-2">
              <Label className="text-sm text-gray-600">
                Current Attendees:
              </Label>
              <AttendeeChips
                emails={currentAttendees}
                onRemove={onRemoveAttendee}
                showRemoveButton={true}
                clickable={false}
                ContactPopoverComponent={ContactPopoverComponent}
              />
            </div>
          )}
        </div>
      </FormSectionCard>
      </div>
      </div>
    </>
  );
};
